import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'
import type { ISvdCard } from '@src/types/svd-product-card.types'

import { FEATURES, MODULES } from '../../constants/template-info'
import QuinceApiError from '../../domains/quince-api-error'
import { FEATURE_HEADERS } from '../../utils/constants'
import { getContentProductsEnv } from '../../utils/getEnvironmentParam'
import {
  storefrontSandProductServiceAuthHttpClient,
  storefrontSandProductServiceHttpClient,
} from '../../utils/HttpClient'
import { ACTION } from '../Wishlist.types'

/**
 * SVD wishlist service — the `/v2/*` wishlist endpoints, keyed by internal `productId` +
 * `selectedAttributes` (the SVD identity). Kept entirely separate from the legacy `wishlistService`
 * (which stays spid+color / v3) so non-SVD behaviour is provably unchanged. All read endpoints return
 * fully-hydrated SVD cards (`ISvdCard`, the same shape as v4 page-data → maps via `mapSvdProduct`).
 *
 * `x-custom-client: dweb` is required by the v2 contract; auth (`Authorization: <idToken>`) is added by
 * the auth HTTP client's interceptor. `x-content-env` is required so the wishlist service can forward
 * `environment` to its own SAND `/products/cards` call — the app already sends it, and the backend now
 * expects web to as well (confirmed with wishlist BE, live on pre-integ 2026-08-06).
 */

const DWEB_HEADER = { 'x-custom-client': 'dweb' }

const getSvdWishlistHeaders = () => ({
  ...DWEB_HEADER,
  [FEATURE_HEADERS.CONTENT_ENV]: getContentProductsEnv(),
})

interface SvdWishlistItemInput {
  productId: string
  selectedAttributes: Record<string, string>
}

class SvdWishlistService {
  /** Authenticated user's active (in-stock) wishlist as hydrated SVD cards. */
  async getActiveWishlist(): Promise<ISvdCard[]> {
    try {
      const { data } = await storefrontSandProductServiceAuthHttpClient.get<{ total: number; data: ISvdCard[] }>(
        '/v2/get-active-wishlist-products',
        { headers: getSvdWishlistHeaders() },
      )
      return data.data ?? []
    } catch (err) {
      captureException(
        new QuinceApiError({
          apiName: 'SVD_GET_ACTIVE_WISHLIST',
          // @i18n-ignore
          message: 'Failed to fetch SVD active wishlist',
          error: err as Error,
        }),
        { module: MODULES.WISHLIST, feature: FEATURES.FETCH_WISHLIST },
      )
      return []
    }
  }

  /** Public shared wishlist (no auth) as hydrated SVD cards. */
  async getSharedWishlist(token: string): Promise<{ userName?: string; cards: ISvdCard[] }> {
    try {
      const { data } = await storefrontSandProductServiceHttpClient.get<{ user_name?: string; wishlist?: ISvdCard[] }>(
        '/v2/get-shared-wishlist',
        { params: { wishlist_token: token }, headers: getSvdWishlistHeaders() },
      )
      return { userName: data.user_name, cards: data.wishlist ?? [] }
    } catch (err) {
      captureException(
        new QuinceApiError({
          apiName: 'SVD_GET_SHARED_WISHLIST',
          // @i18n-ignore
          message: 'Failed to fetch SVD shared wishlist',
          error: err as Error,
        }),
        { module: MODULES.WISHLIST, feature: FEATURES.FETCH_WISHLIST },
      )
      return { cards: [] }
    }
  }

  /**
   * Add/remove a single item, keyed by productId + selectedAttributes. Failures are reported (a
   * dropped backend write leaves the item wishlisted locally but absent server-side — recovered by
   * the login-merge next load, so we surface it to Sentry rather than throw).
   */
  async updateWishlist(productId: string, selectedAttributes: Record<string, string>, action: ACTION): Promise<void> {
    try {
      await storefrontSandProductServiceAuthHttpClient.post(
        '/v2/update-user-wishlist',
        { productId, selectedAttributes, action },
        { headers: getSvdWishlistHeaders() },
      )
    } catch (err) {
      captureException(
        new QuinceApiError({
          apiName: 'SVD_UPDATE_WISHLIST',
          // @i18n-ignore
          message: 'Failed to update SVD wishlist',
          error: err as Error,
          data: { productId, action },
        }),
        { module: MODULES.WISHLIST, feature: FEATURES.TOGGLE_WISHLIST },
      )
    }
  }

  /** Additive bulk push (used on login-merge) — appends without removing existing items. */
  async bulkUpdateWishlist(items: SvdWishlistItemInput[]): Promise<void> {
    if (!items.length) return
    try {
      await storefrontSandProductServiceAuthHttpClient.post('/v2/bulk-update-user-wishlist', items, {
        headers: getSvdWishlistHeaders(),
      })
    } catch (err) {
      captureException(
        new QuinceApiError({
          apiName: 'SVD_BULK_UPDATE_WISHLIST',
          // @i18n-ignore
          message: 'Failed to bulk-update SVD wishlist',
          error: err as Error,
          data: { count: items.length },
        }),
        { module: MODULES.WISHLIST, feature: FEATURES.UPDATE_WISHLIST },
      )
    }
  }
}

const svdWishlistService = new SvdWishlistService()

export default svdWishlistService
